import { getTargetId } from '@/adapter/blockAdapter';
import IconButton from '@/components/common/IconButton';
import { ExtendedCustomNodeProps } from '@/components/flow/NodeRenderer';
import { adjustBrightness, getColorFromString } from '@/util/color';
import TuneIcon from '@mui/icons-material/Tune';
import { Card, CardHeader, Divider, Stack, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import CustomHandleView from './handle/CustomHandleView';

const CustomNodeAlt: FC<ExtendedCustomNodeProps> = ({ id, data, size, ...props }) => {
  const theme = useTheme();

  const headerBgColor = getColorFromString(
    props.blockStyle || data.backendNode.type,
    theme.palette.mode === 'dark' ? 700 : 200
  );

  const cardBgColor = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[50];

  return (
    <Stack direction='row'>
      <Card
        elevation={props.selected ? 5 : 1}
        sx={{
          minWidth: size?.width || '10rem',
          minHeight:
            size?.height || (props.sourceHandles.length <= 2 ? '8rem' : `${2 * props.sourceHandles.length}rem`),
          bgcolor: cardBgColor,
          display: 'flex',
          flexDirection: 'column',
          borderTop: '0.2em solid',
          borderTopColor: headerBgColor
        }}
      >
        <CardHeader
          action={
            <IconButton tooltip='Details' onClick={data.onDetailsClicked} size='small'>
              <TuneIcon fontSize='small' />
            </IconButton>
          }
          title={
            <Typography variant='body2' fontFamily='monospace'>
              {`${data.backendNode.type} - ${id}`}
            </Typography>
          }
          sx={{
            p: 1,
            pt: 0.5,
            pb: 0.5,
            bgcolor: adjustBrightness(cardBgColor, 0.96)
          }}
        />
        <Stack py={1.5} px={1} gap={1} direction='row' alignItems='start' height='100%'>
          {!props.isStartNode && (
            <CustomHandleView
              position={'left'}
              handle={{
                id: getTargetId(id),
                label: '',
                type: 'target'
              }}
            />
          )}
          <Typography variant='body2' sx={{ maxWidth: '10rem', flexGrow: 1 }}>
            {props.label}
          </Typography>
          {props.sourceHandles.some((handle) => !!handle.label) && (
            <>
              <Divider orientation='vertical' />
              <Stack alignItems='end' justifyContent='flex-start' height='100%' gap={1}>
                {props.sourceHandles.map((handle, index) => (
                  <Stack
                    key={`handles.stack.${id}.${handle.id}.${index}`}
                    direction='row'
                    height={16}
                    width='100%'
                    alignItems='center'
                    justifyContent='end'
                    gap={0.5}
                  >
                    <Typography variant='body1' fontSize='0.6em' letterSpacing={0.5}>
                      {handle.label}
                    </Typography>
                    <CustomHandleView handle={handle} position='right' />
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default CustomNodeAlt;
